import { Controller } from "@hotwired/stimulus";

/**
 * Plans Controller - Plan selection & state management

 * Responsibilities:
 * • Plan card selection & visual state (selected/unselected)
 * • Plan button text updates (Select/Change/Keep/Current)
 * • Change mode activation & premium auto-selection
 * • Enterprise vs basic/premium plan mode switching
 * • Inter-controller communication via plan-changed events
 * • Backend-driven category detection via data-category attributes

 * Replaces jQuery:
 * • Plan.selectPlan() - plan card clicking & selection
 * • Plan.change() / Plan.unchange() - change mode toggling
 * • Plan button state management & text updates
 */

export default class extends Controller {
  static DEFAULTS = {
    SELECTED_CLASS: "selected",
    PLAN_ACTION_SELECTED: "plan-action selected-plan",
    PLAN_ACTION_DEFAULT: "plan-action select-plan",
  };

  static targets = [
    "planCard",
    "planButton",
    "cardsWrapper",
    "enterpriseCard",
    "monthlyButton",
    "yearlyButton"
  ];

  static values = {
    selectedPlan: String,
    currentPlan: String,
    currentRecurrence: String,
    recurrence: String
  };

  connect() {
    if (!this.selectedPlanValue) {
      this.selectedPlanValue = this.currentPlanValue || "";
    }

    // Initialize recurrence value
    this.recurrenceValue = this.currentRecurrenceValue || "year";

    // Auto-select premium if no plan is selected
    if (!this.selectedPlanValue) {
      const premium = this.findPlanCardByCategory("premium");
      if (premium) this.selectedPlanValue = premium.dataset.plan;
    }

    this.updatePlanSelection();
    this.updateRecurrenceDisplay();
    this.handleChangeModeSelection();

    // Listen for any plan-changed events (including from pricing controller)
    this.element.addEventListener("plan-changed", this.handlePlanChanged);
  }

  disconnect() {
    this.element.removeEventListener("plan-changed", this.handlePlanChanged);
  }

  // Arrow function automatically binds 'this' context
  handlePlanChanged = (event) => {
    this.changePlanSelection(event.detail.planName);
  };

  selectPlan(event) {
    const planName = event.currentTarget.dataset.plan;
    this.changePlanSelection(planName);
  }

  // Plan helpers

  getCardByPlanName(planName) {
    return this.planCardTargets.find(card => card.dataset.plan === planName);
  }

  getCategoryForPlan(planName) {
    const card = this.getCardByPlanName(planName);
    return card?.dataset.category || "unknown";
  }

  isEnterprise(planName) {
    return this.getCategoryForPlan(planName) === "enterprise";
  }

  isBasicOrPremium(planName) {
    const category = this.getCategoryForPlan(planName);
    return category === "basic" || category === "premium";
  }

  // Centralized plan selection logic
  changePlanSelection(planName) {
    if (planName === this.selectedPlanValue) return;

    this.selectedPlanValue = planName;
    this.updatePlanSelection();
    this.notifyPricingController(planName);
  }

  activateChangeMode() {
    // Toggle change mode UI state
    const plansContainer = document.getElementById("plans");
    if (plansContainer) {
      plansContainer.classList.add("is-changing");
    }

    const plan = this.selectedPlanValue;

    // Only auto-select premium if we don't already have a basic/premium plan selected
    if (this.isBasicOrPremium(plan)) return;

    const premiumCard = this.findPlanCardByCategory("premium");
    if (premiumCard) this.changePlanSelection(premiumCard.dataset.plan);
  }

  findPlanCardByCategory(category) {
    return this.planCardTargets.find(card => card.dataset.category === category);
  }

  updatePlanSelection() {
    const selectedCategory = this.getCategoryForPlan(this.selectedPlanValue);

    this.planCardTargets.forEach(card => {
      const cardCategory = card.dataset.category;
      const isSelected = selectedCategory === cardCategory;

      if (cardCategory !== "enterprise") {
        card.classList.toggle(this.constructor.DEFAULTS.SELECTED_CLASS, isSelected);

        const button = card.querySelector("[data-plans-target='planButton']");
        if (button) {
          this.updateButtonState(button, isSelected);
        }
      }
    });

    // Update recurrence display based on selected plan
    this.updateRecurrenceDisplay();
  }

  // Toggles button text between default ("Subscribe") and selected ("Selected plan") states
  updateButtonState(button, isSelected) {
    const defaultSpan = button.querySelector("[data-plan-button-text='default']");
    const selectedSpan = button.querySelector("[data-plan-button-text='selected']");

    if (defaultSpan && selectedSpan) {
      if (isSelected) {
        defaultSpan.style.display = "none";
        selectedSpan.style.display = "inline";
        button.className = this.constructor.DEFAULTS.PLAN_ACTION_SELECTED;
      } else {
        defaultSpan.style.display = "inline";
        selectedSpan.style.display = "none";
        button.className = button.dataset.defaultClass || this.constructor.DEFAULTS.PLAN_ACTION_DEFAULT;
      }
    }
  }

  handleChangeModeSelection() {
    const plansContainer = document.getElementById("plans");
    if (plansContainer && plansContainer.classList.contains("is-changing")) {
      this.activateChangeMode();
    }
  }

  updateRecurrenceDisplay() {
    const isMonthly = this.recurrenceValue === "month";
    const isEnterprise = this.isEnterprise(this.selectedPlanValue);

    this.monthlyButtonTargets.forEach(btn => btn.classList.toggle("active", isMonthly && !isEnterprise));
    this.yearlyButtonTargets.forEach(btn => btn.classList.toggle("active", !isMonthly && !isEnterprise));
  }

  notifyPricingController(planName) {
    this.dispatch("plan-changed", { detail: { planName }, bubbles: true });
  }

  setRecurrence(event) {
    event.preventDefault();

    const period = event.currentTarget.dataset.period;
    if (!["month", "year"].includes(period)) return;

    this.recurrenceValue = period;
    this.updateRecurrenceDisplay();

    // Notify pricing controller
    this.dispatch("recurrence-changed", {
      detail: { recurrence: period },
      bubbles: true
    });
  }

  showEnterprise(event) {
    event?.preventDefault();
    event?.stopPropagation();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.add("enterprise-selected");

    const enterpriseCard = this.findPlanCardByCategory("enterprise");
    if (enterpriseCard) {
      this.changePlanSelection(enterpriseCard.dataset.plan);
    }
  }

  showBasicPremium(event) {
    event?.preventDefault();

    this.hasCardsWrapperTarget && this.cardsWrapperTarget.classList.remove("enterprise-selected");

    this.activateChangeMode();
  }

  buildPlan() {
    // TODO: Implement in a later PR - submit flow
    console.log("Plan submission not yet implemented");
  }
}
